import { Link } from "@tanstack/react-router";
import { AlertTriangle, Clock } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ProgressBar } from "@/components/shared/progress-bar";
import { EmptyState } from "@/components/shared/empty-state";
import type { AtRiskKr } from "@/hooks/use-dashboard";

interface AtRiskListProps {
  title: string;
  keyResults: AtRiskKr[];
  emptyMessage?: string;
}

function formatReason(kr: AtRiskKr): string {
  if (kr.reason === "stale") {
    if (kr.last_updated_at) {
      const days = Math.floor(
        (Date.now() - new Date(kr.last_updated_at).getTime()) / (1000 * 60 * 60 * 24),
      );
      return `No updates in ${days} days`;
    }
    return "No recent updates";
  }
  const gap = Math.round((kr.expected_percent ?? 0) - kr.completion_percent);
  return `${gap} points behind expected pace`;
}

function ReasonBadge({ reason }: { reason: AtRiskKr["reason"] }) {
  if (reason === "stale") {
    return (
      <Badge variant="secondary" className="gap-1">
        <Clock className="h-3 w-3" />
        Stale
      </Badge>
    );
  }
  return (
    <Badge variant="destructive" className="gap-1">
      <AlertTriangle className="h-3 w-3" />
      Behind Pace
    </Badge>
  );
}

export function AtRiskList({
  title,
  keyResults,
  emptyMessage = "No at-risk key results.",
}: AtRiskListProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          {title}
          {keyResults.length > 0 && (
            <Badge variant="destructive">{keyResults.length}</Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {keyResults.length === 0 ? (
          <EmptyState title="All clear" description={emptyMessage} />
        ) : (
          <ul className="space-y-3">
            {keyResults.map((kr) => (
              <li key={kr.id}>
                <Link
                  to="/key-results/$id"
                  params={{ id: kr.id }}
                  className="block rounded-lg border p-4 transition-colors hover:bg-[hsl(var(--muted))]"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-medium">{kr.title}</p>
                      <p className="mt-1 text-sm text-[hsl(var(--muted-foreground))]">
                        {formatReason(kr)}
                      </p>
                    </div>
                    <ReasonBadge reason={kr.reason} />
                  </div>
                  <div className="mt-3 flex items-center gap-3">
                    <ProgressBar value={kr.completion_percent} className="flex-1" />
                    <span className="shrink-0 text-sm font-medium">
                      {Math.round(kr.completion_percent)}%
                    </span>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
